import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Header } from '../components/Header';
import { SearchBar } from '../components/SearchBar';
import { FilterPanel } from '../components/FilterPanel';
import { ToolCard } from '../components/ToolCard';
import { tools } from '../data/tools';
import { SlidersHorizontalIcon, XIcon } from 'lucide-react';
import { SEO } from '../components/SEO';
export function SearchResultsPage() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedPricing, setSelectedPricing] = useState<string | null>(null);
  const [selectedEase, setSelectedEase] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const filteredTools = useMemo(() => {
    let filtered = tools;
    if (query) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(tool => tool.name.toLowerCase().includes(lowerQuery) || tool.description.toLowerCase().includes(lowerQuery) || tool.category.toLowerCase().includes(lowerQuery) || tool.bestFor.toLowerCase().includes(lowerQuery));
    }
    if (selectedCategory) {
      filtered = filtered.filter(tool => tool.category === selectedCategory);
    }
    if (selectedPricing) {
      filtered = filtered.filter(tool => tool.pricing === selectedPricing);
    }
    if (selectedEase) {
      filtered = filtered.filter(tool => tool.ease === selectedEase);
    }
    return filtered;
  }, [query, selectedCategory, selectedPricing, selectedEase]);
  const activeFilterCount = [selectedCategory, selectedPricing, selectedEase].filter(Boolean).length;
  return <div className="min-h-screen bg-white" data-id="element-544">
      <SEO title="Search AI Tools — Find the Perfect AI Tool" description="Search and filter 50+ curated AI tools by category, task, pricing and more. Find the right AI tool for writing, design, video, code, audio or research." keywords="search AI tools, find AI tools, AI tool search, filter AI tools, compare AI tools" canonicalPath="/search" data-id="element-545" />
      <Header data-id="element-546" />

      <main className="w-full" data-id="element-547">
        {/* Search Bar */}
        <section className="border-b border-grey-200 bg-white sticky top-[89px] sm:top-[97px] z-30" data-id="element-548">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6" data-id="element-549">
            <SearchBar defaultValue={query} size="medium" data-id="element-550" />
          </div>
        </section>

        {/* Results Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-id="element-551">
          {/* Mobile Filter Toggle */}
          <div className="flex items-center justify-between mb-6 lg:hidden" data-id="element-552">
            <p className="text-sm text-grey-600" data-id="element-553">
              {filteredTools.length}{' '}
              {filteredTools.length === 1 ? 'result' : 'results'}
              {query && ` for "${query}"`}
            </p>
            <button onClick={() => setMobileFiltersOpen(true)} className="flex items-center gap-2 px-4 py-2 rounded-full bg-grey-100 hover:bg-grey-200 text-sm font-medium text-grey-700 transition-colors" data-id="element-554">
              <SlidersHorizontalIcon className="w-4 h-4" data-id="element-555" />
              Filters
              {activeFilterCount > 0 && <span className="flex items-center justify-center w-5 h-5 rounded-full bg-black text-white text-xs font-bold" data-id="element-556">
                  {activeFilterCount}
                </span>}
            </button>
          </div>

          {/* Mobile Filter Drawer */}
          <AnimatePresence data-id="element-557">
            {mobileFiltersOpen && <div className="fixed inset-0 z-[100] lg:hidden" data-id="element-558">
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setMobileFiltersOpen(false)} data-id="element-559" />
                <motion.div initial={{
              x: '100%'
            }} animate={{
              x: 0
            }} exit={{
              x: '100%'
            }} transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30
            }} className="absolute right-0 top-0 bottom-0 w-[280px] bg-white shadow-2xl z-10 p-6 overflow-y-auto" data-id="element-560">
                  <div className="flex items-center justify-between mb-6" data-id="element-561">
                    <h2 className="text-lg font-bold text-black" data-id="element-562">Filters</h2>
                    <button onClick={() => setMobileFiltersOpen(false)} className="p-2 rounded-full hover:bg-grey-100 transition-colors" aria-label="Close filters" data-id="element-563">
                      <XIcon className="w-5 h-5 text-grey-500" data-id="element-564" />
                    </button>
                  </div>
                  <FilterPanel selectedCategory={selectedCategory} selectedPricing={selectedPricing} selectedEase={selectedEase} onCategoryChange={setSelectedCategory} onPricingChange={setSelectedPricing} onEaseChange={setSelectedEase} data-id="element-565" />
                  <button onClick={() => setMobileFiltersOpen(false)} className="w-full mt-8 bg-black text-white py-3 rounded-full text-sm font-semibold hover:bg-grey-800 transition-colors" data-id="element-566">
                    Show {filteredTools.length} results
                  </button>
                </motion.div>
              </div>}
          </AnimatePresence>

          <div className="flex gap-8 lg:gap-12" data-id="element-567">
            {/* Desktop Filter Panel */}
            <motion.aside initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.4
          }} className="hidden lg:block shrink-0" data-id="element-568">
              <FilterPanel selectedCategory={selectedCategory} selectedPricing={selectedPricing} selectedEase={selectedEase} onCategoryChange={setSelectedCategory} onPricingChange={setSelectedPricing} onEaseChange={setSelectedEase} data-id="element-569" />
            </motion.aside>

            {/* Results Grid */}
            <div className="flex-1 min-w-0" data-id="element-570">
              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.1
            }} className="mb-6 hidden lg:block" data-id="element-571">
                <p className="text-sm text-grey-600" data-id="element-572">
                  {filteredTools.length}{' '}
                  {filteredTools.length === 1 ? 'result' : 'results'}
                  {query && ` for "${query}"`}
                </p>
              </motion.div>

              <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6" data-id="element-573">
                {filteredTools.map((tool, index) => <motion.div key={tool.id} initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.3,
                delay: 0.3 + index * 0.05
              }} data-id="element-574">
                    <ToolCard tool={tool} data-id="element-575" />
                  </motion.div>)}
              </motion.div>

              {filteredTools.length === 0 && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} className="text-center py-16" data-id="element-576">
                  <p className="text-grey-600" data-id="element-577">
                    No tools found matching your criteria.
                  </p>
                  <p className="text-sm text-grey-500 mt-2" data-id="element-578">
                    Try adjusting your filters or search query.
                  </p>
                </motion.div>}
            </div>
          </div>
        </section>
      </main>
    </div>;
}